/**
 * PR #41 final hardening — Phase 4 (modal + navigation regression
 * verification).
 *
 * Single source of truth for the storefront's tenant-scoped
 * sessionStorage key formats. Extracted because two return-flow pages
 * (`RetailPaymentReturn.jsx` after a successful Stripe payment,
 * `RetailOrderStatus.jsx`'s "Start New Order"/error "Back to menu")
 * were clearing the UNSCOPED legacy key `"retail_customer_v1"` — a
 * stale reference from before the cross-tenant session-leak fix that
 * scoped this key to `retail_customer_v1:{slug}` — so "end the
 * customer's session after payment" silently did nothing. Both pages
 * now import `customerKey` from here instead of re-typing the format.
 */

const CUSTOMER_KEY = "retail_customer_v1";
const LINK_SLUG_KEY = "retail_link_slug_v1";
const ORDER_ACCESS_KEY = "retail_order_access_v1";
const VERIFIED_PROOF_KEY = "retail_verified_proof_v1";

export function customerKey(slug) {
  return `${CUSTOMER_KEY}:${slug || "unknown"}`;
}

// The catalog-specific entry `linkSlug` (route `/retail/:slug/:linkSlug`)
// a customer originally arrived through. Persisted so a later "back to
// storefront" navigation (order status page, payment return) can rebuild
// the SAME catalog-specific URL instead of falling through to the bare
// `/retail/:slug` route, which re-resolves to "any published catalog"
// for the tenant — silently swapping a Parcel Shipping-only catalog for
// an unrelated one.
export function linkSlugKey(slug) {
  return `${LINK_SLUG_KEY}:${slug || "unknown"}`;
}

export function saveLinkSlug(slug, linkSlug) {
  try {
    if (linkSlug) sessionStorage.setItem(linkSlugKey(slug), linkSlug);
  } catch (_e) { /* noop — session persistence is best-effort */ }
}

export function loadLinkSlug(slug) {
  try {
    return sessionStorage.getItem(linkSlugKey(slug)) || "";
  } catch (_e) {
    return "";
  }
}

// Builds the correct "back to this storefront" path — catalog-specific
// when a linkSlug was recorded for this tenant this session, bare
// otherwise (a tenant with no link profiles has nothing to lose by
// falling back to `/retail/:slug`).
export function storefrontPath(slug) {
  const linkSlug = loadLinkSlug(slug);
  return linkSlug ? `/retail/${slug}/${linkSlug}` : `/retail/${slug}`;
}

// Security correction (Aug 2026) — the per-order access credential
// (`retail/orders.py`'s `_generate_order_access_token`) that now
// authorizes the public order-status/receipt/cancel routes. Scoped by
// BOTH tenant slug and order_code (never a single blanket slot) so
// multiple orders across the same tab/session never collide or
// overwrite one another. Deliberately `sessionStorage`, never
// `localStorage` — this credential is meant for the immediate,
// same-tab status/return experience; a lost/cleared/different-device
// session recovers access via fresh OTP verification
// (`POST /api/public/retail/{slug}/orders/{order_code}/recover-access`),
// never by falling back to a durable, silently-scoped browser secret.
function orderAccessKey(slug, orderCode) {
  return `${ORDER_ACCESS_KEY}:${slug || "unknown"}:${orderCode || "unknown"}`;
}

export function saveOrderAccessToken(slug, orderCode, token) {
  try {
    if (slug && orderCode && token) {
      sessionStorage.setItem(orderAccessKey(slug, orderCode), token);
    }
  } catch (_e) { /* noop — session persistence is best-effort */ }
}

export function loadOrderAccessToken(slug, orderCode) {
  try {
    return sessionStorage.getItem(orderAccessKey(slug, orderCode)) || "";
  } catch (_e) {
    return "";
  }
}

// Merge-blocker correction (Aug 2026) — Customer Favorites refresh
// continuity. Persisted the SAME way `orderAccessKey` above already
// persists its own opaque, backend-revalidated credential:
// `sessionStorage`, never `localStorage`, scoped by tenant slug so it
// can never be read back under a different tenant's page. This is
// deliberately not a new security posture — it's this exact
// established pattern, applied to a second opaque proof artifact.
//
// Security-audit correction (Aug 2026, round 2) — the FIRST version of
// this helper also stored `destinationValue` (the customer's raw phone
// number or email address) alongside the token, on the theory that
// `POST /recognize` needed it to re-validate. That was genuine,
// unmasked customer PII sitting in browser storage — unacceptable
// regardless of scoping, and especially so on a SHARED KIOSK tablet
// where the next customer could read it from the same tab. Masking or
// hashing it client-side would not have fixed this: `_hash_destination`
// (`retail/customer_order_history.py`) is an UNSALTED SHA-256 over a
// low-entropy value (a phone number has only ~10^10 possibilities),
// making it dictionary-crackable — not real protection.
//
// The backend (`load_verified_customer`) now supports a genuinely
// opaque, token-only restore: it opportunistically caches the
// customer id it already resolves on every LIVE call (right after OTP
// verification, when phone/email are still in memory, never written
// here) onto the underlying `retail_order_otps` row itself, keyed by
// `checkout_attempt_token` — which is already the actual 192-bit
// secret this whole mechanism is built on. A later restore call
// carrying ONLY the token can therefore resolve the SAME customer
// without ever re-supplying (or this file ever storing) the raw
// destination. See that function's own docstring for the full
// rationale and why dropping the destination-match requirement for
// THIS path carries no real security cost.
//
// What is stored here: `channel` ("sms"/"email" — a MODE, not PII),
// `token` (opaque, meaningless without server-side revalidation),
// `catalogId`, `verifiedAt`. What is NEVER stored here: the phone
// number or email itself, customer_id, membership status, or any
// Favorites data. An expired, revoked, wrong-tenant, or otherwise
// invalid/unresolved token restores nothing — the backend returns
// `{customer: null, member: null}` exactly as it does for an
// unverified caller.
//
// `sessionStorage` (not `localStorage`) means this survives a same-tab
// refresh — the actual requirement — but is gone once the tab/browser
// closes, matching "refresh continuity, not permanent login."
function verifiedProofKey(slug) {
  return `${VERIFIED_PROOF_KEY}:${slug || "unknown"}`;
}

export function saveVerifiedProof(slug, proof) {
  try {
    if (!slug || !proof || !proof.token) return;
    const stored = {
      channel: proof.channel || null,
      token: proof.token,
      catalogId: proof.catalogId || null,
      verifiedAt: proof.verifiedAt || null,
    };
    sessionStorage.setItem(verifiedProofKey(slug), JSON.stringify(stored));
  } catch (_e) { /* noop — session persistence is best-effort */ }
}

export function loadVerifiedProof(slug) {
  try {
    const raw = sessionStorage.getItem(verifiedProofKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.token) return null;
    // Defense in depth — a malformed/tampered/legacy stored value that
    // somehow carries a `destinationValue` (or any other unexpected
    // key) is never returned as-is; only the exact approved shape ever
    // leaves this function, so a stray PII field can never silently
    // travel further even if it were ever written by mistake.
    return {
      channel: parsed.channel || null,
      token: parsed.token,
      catalogId: parsed.catalogId || null,
      verifiedAt: parsed.verifiedAt || null,
    };
  } catch (_e) {
    return null;
  }
}

export function clearVerifiedProof(slug) {
  try {
    sessionStorage.removeItem(verifiedProofKey(slug));
  } catch (_e) { /* noop */ }
}
